import React, { useState, useEffect, useRef } from 'react';

// Shared lead-time control for quotes and sales orders.
//
// Jesse's ask (2026-07-23): keep the three named service-level presets, but
// replace the fixed "3/4/6/8/12 Weeks" options with a free-form entry — pick a
// unit (Days / Weeks / Months) and type a number, e.g. "5 Weeks".
//
// The stored `lead_time` value is a plain display string end-to-end (quotes /
// sales_orders columns are Optional[str]; it is only shown on the SO/quote and
// printed on the quote PDF — nothing parses it into a schedule). So a custom
// value like "5 Weeks" flows through save, quote->SO conversion, and display
// exactly like the old fixed options did.

// The named presets kept from the original dropdown ("keep these").
export const LEAD_TIME_PRESETS = [
    'Standard (10-14 Days)',
    'Expedited (3-5 Days)',
    'Rush (1-2 Days)',
];

const CUSTOM = '__custom__';
const UNITS = ['Days', 'Weeks', 'Months'];

// Build a natural string from a number + unit: (5, "Weeks") -> "5 Weeks",
// (1, "Week"|"Weeks") -> "1 Week". Returns '' for a missing/non-positive number
// so an in-progress custom entry reads as "not set" rather than a broken string.
export function formatLeadTime(number, unit) {
    const raw = String(number == null ? '' : number).trim();
    if (raw === '' || !(parseFloat(raw) > 0)) return '';
    const singular = parseFloat(raw) === 1;
    const label = singular ? unit.replace(/s$/, '') : unit;
    return `${raw} ${label}`;
}

// Parse a stored lead_time string back into control state.
export function parseLeadTime(value) {
    const v = (value == null ? '' : String(value)).trim();
    if (LEAD_TIME_PRESETS.includes(v)) {
        return { mode: v, number: '', unit: 'Weeks' };
    }
    const m = v.match(/^(\d+(?:\.\d+)?)\s*(day|days|week|weeks|month|months)$/i);
    if (m) {
        const u = m[2].toLowerCase();
        const unit = u.startsWith('day') ? 'Days' : u.startsWith('month') ? 'Months' : 'Weeks';
        return { mode: CUSTOM, number: m[1], unit };
    }
    if (v === '') {
        // Unset -> default to the first preset so a new quote reads sensibly.
        return { mode: LEAD_TIME_PRESETS[0], number: '', unit: 'Weeks' };
    }
    // Unrecognised free text (rare/legacy) -> treat as custom, leave the inputs
    // blank so a save that doesn't touch this field can't clobber the value.
    return { mode: CUSTOM, number: '', unit: 'Weeks' };
}

export default function LeadTimeSelector({ value, onChange }) {
    const [{ mode, number, unit }, setState] = useState(() => parseLeadTime(value));
    // Tracks the last string we emitted so an external `value` change (e.g. a
    // different quote/SO loads) re-syncs the control, while our own emits don't.
    const lastEmitted = useRef(value);

    useEffect(() => {
        if (value === lastEmitted.current) return;
        setState(parseLeadTime(value));
        lastEmitted.current = value;
    }, [value]);

    const emit = (next) => {
        lastEmitted.current = next;
        onChange(next);
    };

    const handleModeChange = (e) => {
        const next = e.target.value;
        setState((s) => ({ ...s, mode: next }));
        emit(next === CUSTOM ? formatLeadTime(number, unit) : next);
    };

    const handleNumberChange = (e) => {
        const n = e.target.value;
        setState((s) => ({ ...s, number: n }));
        emit(formatLeadTime(n, unit));
    };

    const handleUnitChange = (e) => {
        const u = e.target.value;
        setState((s) => ({ ...s, unit: u }));
        emit(formatLeadTime(number, u));
    };

    return (
        <div className="d-flex align-items-center gap-2 flex-wrap">
            <select
                className="form-select form-select-sm"
                style={{ maxWidth: '200px' }}
                value={mode}
                onChange={handleModeChange}
            >
                {LEAD_TIME_PRESETS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                ))}
                <option value={CUSTOM}>Custom…</option>
            </select>
            {mode === CUSTOM && (
                <div className="d-flex align-items-center gap-1">
                    <input
                        type="number"
                        min="1"
                        step="1"
                        className="form-control form-control-sm"
                        style={{ width: '80px' }}
                        placeholder="e.g. 5"
                        value={number}
                        onChange={handleNumberChange}
                    />
                    <select
                        className="form-select form-select-sm"
                        style={{ maxWidth: '120px' }}
                        value={unit}
                        onChange={handleUnitChange}
                    >
                        {UNITS.map((u) => (
                            <option key={u} value={u}>{u}</option>
                        ))}
                    </select>
                </div>
            )}
        </div>
    );
}
